import React, { useEffect, useState, useCallback  } from "react";
import Map from "ol/Map";
import View from "ol/View";
import GeoJSON from "ol/format/GeoJSON";
import TileLayer from "ol/layer/Tile";
import VectorLayer from "ol/layer/Vector";
import "ol/ol.css";
import Style from "ol/style/Style";
import Fill from "ol/style/Fill";
import Stroke from "ol/style/Stroke";
import XYZ from "ol/source/XYZ";
import VectorSource from "ol/source/Vector";
import { Projection } from "ol/proj";
import ImageWMS from "ol/source/ImageWMS";
import Image from "ol/layer/Image";
import { getWFSLayer, postDataWithBodyFrGIS, getWFSServiceData } from "../../../../../Services/Services";
import { FullScreen, useFullScreenHandle } from "react-full-screen";
import Overlay from "ol/Overlay.js";
import Accordion from "react-bootstrap/Accordion";
import Card from "react-bootstrap/Card";
import { toLonLat } from "ol/proj.js";
import { toStringHDMS } from "ol/coordinate.js";

/**
 * SOPInstanceOpenLayerSource Component
 * 
 * A React component that displays an interactive map for disaster management incidents.
 * Features include:
 * - Real-time incident tracking and visualization
 */
const SOPInstanceOpenLayerSource = (props) => {
  // ===== STATE MANAGEMENT =====
  
  // Core map instance and dependencies
  const [map, setMap] = useState(null);
  const [mapdependency, setMapdependency] = useState(1); // Triggers map-related effects
  const [incidentdependency, setIncidentdependency] = useState(1); // Triggers incident loading effects
  
  // Incident data states
  const [incident, setIncident] = useState(null); // Complete incident data
  const [incidentbasic, setIncidentbasic] = useState(null); // Basic incident information
  const [incidentregion, setIncidentregion] = useState(null); // Region-specific incident data
  const [incidentgis, setIncidentgis] = useState(null); // GIS data for regions
  const [incidentid, setIncidentid] = useState(""); // Current active incident ID
  
  // Scheduler and auto-refresh
  const [startScheduler, setStartScheduler] = useState(false); // Controls auto-refresh
  
  // UI layout states
  const [mapwidth, setMapwidth] = useState("col-12 g-1"); // Map container width
  const [infoWidth, setInfoWidth] = useState("col-0 g-1"); // Info panel width
  const [infoStyle, setInfoStyle] = useState("none"); // Info panel display style
  const [regionView, setRegionView] = useState("78vh"); // Region list height
  
  // Map layers and overlays
  const [eventlayer, setEventlayer] = useState(null); // WMS layer for events
  const [content, setContent] = useState(null); // Popup content element
  const [overlay, setOverlay] = useState(null); // Map overlay for popups
  
  // ===== CONFIGURATION =====
  
  // Extract props
  const center = props.center; // Map center coordinates
  const zoom = props.zoom; // Initial zoom level
  const gisserverurl = process.env.REACT_APP_GEO_SERVER; // GeoServer URL from environment
  
  // Fullscreen handling
  const handle = useFullScreenHandle();
  
  // ===== MAP SETUP =====
  
  // Define map projection (WGS84 - World Geodetic System 1984)
  var defprojection = new Projection({
    code: "EPSG:4326",
    units: "degrees",
    axisOrientation: "neu",
  });
  
  // Configure map view with center, projection, and zoom
  const view = new View({
    center: [center.lng, center.lat],
    projection: defprojection,
    zoom: zoom,
  });
  
  // Base layer - Google Maps tiles
  const sourceLayer = new TileLayer({
    source: new XYZ({
      url: "http://mt0.google.com/vt/lyrs=m&hl=en&x={x}&y={y}&z={z}",
      maxZoom: 18,
    }),
  });
  
  // Vector layer for incident regions
  const vectsource = new VectorSource({ wrapX: false });
  const vector = new VectorLayer({
    source: vectsource,
  });
  
  // ===== FULLSCREEN HANDLING =====
  
  /**
   * Callback for fullscreen state changes
   * Adjusts the region view height based on fullscreen state
   */
  const reportChange = useCallback(
    (state, handle1) => {
      if (state) {
        setRegionView("92vh"); // Larger height in fullscreen
      } else {
        setRegionView("78vh"); // Normal height
      }
    },
    [handle]
  );

  // ===== EFFECT HOOKS =====
  
  // Initialize map on component mount
  useEffect(() => {
    initMap();
  }, []);

  // Load incident data when map dependency changes
  useEffect(() => {
    loadcurrentincident();
  }, [mapdependency]);

  // Load incident GIS data to map when incident dependency changes
  useEffect(() => {
    if (incidentgis) {
      loadincidenttomap();
    }
  }, [incidentdependency]);

  // Start auto-refresh scheduler
  useEffect(() => {
    if (startScheduler) {
      showincident(); // Show incident events on map
      setInterval(loadcurrentincident, 15000); // Refresh every 15 seconds
    }
  }, [startScheduler]);
  
  // Enable map click events when event layer is ready
  useEffect(() => {
    if (map != null) {
      enableclickevent();
    }
  }, [eventlayer]);

  // ===== INCIDENT DATA LOADING =====
  
  /**
   * Loads the current active incident and its details
   * Fetches incident data from the API and processes region information
   */
  const loadcurrentincident = async () => {
    // Get all active incidents
    const activeincidents = await postDataWithBodyFrGIS(
      "/getData/getAllincidents",
      {},
      {}
    );
    
    if (activeincidents["data"] && activeincidents["data"][0]) {
      const activeinst = activeincidents["data"][0];
      const input = { incidentid: activeinst["id"] };
      setIncidentid(activeinst["id"]);
      
      // Get detailed incident information
      const incidentdetails = await postDataWithBodyFrGIS(
        "/getAllData/incidentdtls",
        input,
        {}
      );
      
      // Update incident states
      setIncident(incidentdetails);
      setIncidentbasic(incidentdetails.basicinfo[0]);
      
      let regions = incidentdetails["regions"];
      
      // Check if regions have been updated or are new
      if (
        incidentregion != null ||
        JSON.stringify(regions) != JSON.stringify(incidentregion)
      ) {
        // Process region updates and status changes
        regions = checkIncidentUpgrade(regions);
        setIncidentregion(regions);
        
        // Extract GIS IDs for mapping
        let gisids = [];
        regions.forEach((element) => {
          gisids.push(element["gis_id"]);
        });
        
        // Load GIS data for the regions
        getincidentgisdata(gisids, regions);
      }
    }
  };
  
  /**
   * Checks for incident region upgrades and status changes
   */
  const checkIncidentUpgrade = (regions) => {
    if (incidentregion != null) {
      regions.forEach((newregion) => {
        let isNew = true;
        
        // Compare with existing regions
        incidentregion.forEach((oldregion) => {
          if (newregion.gis_id === oldregion.gis_id) {
            isNew = false;
            
            // Check for status changes
            if (newregion.code != oldregion.code) {
              newregion["statusupdated"] = true;
              newregion["oldstatus"] = oldregion.value;
              newregion["shortkey"] = 2; // Priority for status updated regions
            } else if (oldregion.statusupdated) {
              // Preserve existing update status
              newregion["statusupdated"] = true;
              newregion["oldstatus"] = oldregion.oldstatus;
              newregion["shortkey"] = oldregion.shortkey;
            }
          }
        });
        
        // Mark new regions
        if (isNew) {
          newregion["newregion"] = true;
          newregion["shortkey"] = 1; // Highest priority for new regions
        } else if (!newregion["shortkey"]) {
          newregion["shortkey"] = 3; // Normal priority for unchanged regions
        }
      });
    }
    
    // Sort regions by priority (shortkey)
    regions = regions.sort((a, b) => {
      if (a.shortkey < b.shortkey) {
        return -1;
      }
    });
    return regions;
  };
  
  /**
   * Fetches GIS data for incident regions from WFS service
   */
  const getincidentgisdata = async (regionids, regions) => {
    // Construct WFS request URL
    const url =
      "/geoserver/wfs?service=wfs&version=1.0.0&request=getfeature&typename=dms:panchayat&outputFormat=application/json";
    
    // Create filter for specific regions
    let nodestrs = regionids.join();
    let filter = "objectid in (" + nodestrs + ")";
    const encodedcqlfilter = encodeURI(filter);
    
    // Fetch GIS data
    const wfsdata = await getWFSServiceData(url, encodedcqlfilter);
    
    // Merge region status data with GIS features
    wfsdata.features.forEach((feature) => {
      regions.forEach((region) => {
        if (feature.properties.objectid + "" === region.gis_id) {
          Object.assign(feature.properties, region);
        }
      });
    });
    
    setIncidentgis(wfsdata);
    setIncidentdependency((prev) => prev + 1); // Trigger map update
  };
  
  /**
   * Adds incident region data to the map as a vector layer
   * Applies styling based on region status
   */
  const loadincidenttomap = () => {
    // Create vector source from GeoJSON data
    const source = new VectorSource({
      features: new GeoJSON().readFeatures(incidentgis),
    });

    // Create styled vector layer
    const layer = new VectorLayer({
      source: source,
      style: styleFunction, // Apply custom styling
    });
    
    map.addLayer(layer);
    
    // Start auto-refresh if not already started
    if (!startScheduler) {
      setStartScheduler(true);
    }
  };
  
  /**
   * Style function for incident regions based on their status
   */
  const styleFunction = function (feature) {
    let type = "INCIDENT_REGION_NORMAL";
    let stroke = "", fill = "";
    
    // Find the region status for this feature
    incident["regions"].forEach((element) => {
      if (parseInt(element["gis_id"]) === feature.get("objectid")) {
        type = element["code"];
      }
    });
    
    // Set colors based on region status
    if (type === "INCIDENT_REGION_NORMAL") {
      fill = "#93ea76"; // Green for normal
      stroke = "#3b5e2f";
    } else if (type === "INCIDENT_REGION_WATCH") {
      fill = "#FFEC33"; // Yellow for watch
      stroke = "#3b5e2f";
    } else if (type === "INCIDENT_REGION_WARN") {
      fill = "#FF9633"; // Orange for warning
      stroke = "#3b5e2f";
    } else if (type === "INCIDENT_REGION_AFFECT") {
      fill = "#FF5733"; // Red for affected
      stroke = "#3b5e2f";
    }
    
    // Create and return style
    var retStyle = new Style({
      stroke: new Stroke({
        color: stroke,
        width: 2,
      }),
      fill: new Fill({
        color: fill,
      }),
    });
    return retStyle;
  };
  
  /**
   * Displays incident events on the map using WMS layer
   * Shows real-time event data filtered by current incident ID
   */
  const showincident = () => {
    // Create WMS layer for events
    let wmslayer = new Image({
      source: new ImageWMS({
        ratio: 1,
        url: gisserverurl + "/geoserver/wms",
        params: {
          FORMAT: "image/png",
          VERSION: "1.1.1",
          STYLES: "",
          LAYERS: "dms:event",
          exceptions: "application/vnd.ogc.se_inimage",
          TIMESTAMP: new Date().getTime(), // Cache busting
        },
      }),
    });
    
    // Apply filter to show only current incident events
    var filterParams = {};
    filterParams["CQL_FILTER"] = '"incident_id" IN (' + incidentid + ")";
    wmslayer.getSource().updateParams(filterParams);
    
    map.addLayer(wmslayer);
    setEventlayer(wmslayer);
  };
  
  /**
   * Initializes the OpenLayers map with base layers and popup overlay
   */
  const initMap = () => {
    // Setup popup elements
    const container = document.getElementById("popup");
    let content1 = document.getElementById("popup-content");
    const closer = document.getElementById("popup-closer");
    setContent(content1);
    
    // Create popup overlay
    let overlay1 = new Overlay({
      element: container,
      autoPan: {
        animation: {
          duration: 250,
        },
      },
    });
    
    // Popup close button handler
    closer.onclick = function () {
      overlay1.setPosition(undefined);
      closer.blur();
      return false;
    };

    // Create WMS layer for state boundaries
    let wmslayer = new Image({
      source: new ImageWMS({
        ratio: 1,
        url: gisserverurl + "/geoserver/wms",
        params: {
          FORMAT: "image/png",
          VERSION: "1.1.1",
          STYLES: "",
          LAYERS: "dms:state",
          exceptions: "application/vnd.ogc.se_inimage",
        },
      }),
    });
    
    // Initialize map with layers and overlay
    const map = new Map({
      target: "map",
      layers: [sourceLayer, vector, wmslayer],
      overlays: [overlay1],
      view: view,
    });
    
    // Notify parent component that map is loaded
    props.onmapload(map);
    
    // Update component state
    setMap(map);
    setMapdependency((prev) => prev + 1);
    setOverlay(overlay1);
  };

  /**
   * Enables click event handling on the map
   */
  const enableclickevent = () => {
    map.on("singleclick", function (evt) {
      
      // Check if click hit any vector features (regions)
      const feature = map.forEachFeatureAtPixel(
        evt.pixel,
        function (feature, layer) {
          return feature;
        }
      );
      
      
      if (feature) {
        // Handle region feature click
        const featuredtls = feature.getProperties();
        const hdms = toStringHDMS(toLonLat(evt.coordinate));
        let statushtml = "";
        
        // Generate status badge HTML based on region code
        if (featuredtls.code && featuredtls.code === "INCIDENT_REGION_WARN") {
          statushtml = `<span style= "background-color:#FF9633;color:#000;border-radius:4px;padding:0 5px 0 5px;">Warning</span>`;
        } else if (
          featuredtls.code &&
          featuredtls.code === "INCIDENT_REGION_NORMAL"
        ) {
          statushtml = `<span style= "background-color:#93ea76;color:#000;border-radius:4px;padding:0 5px 0 5px;">Normal</span>`;
        } else if (
          featuredtls.code &&
          featuredtls.code === "INCIDENT_REGION_WATCH"
        ) {
          statushtml = `<span style= "background-color:#FFEC33;color:#000;border-radius:4px;padding:0 5px 0 5px;">Watch</span>`;
        } else if (
          featuredtls.code &&
          featuredtls.code === "INCIDENT_REGION_AFFECT"
        ) {
          statushtml = `<span style= "background-color:#FF5733;color:#000;border-radius:4px;padding:0 5px 0 5px;">Affected</span>`;
        }
        
        // Populate popup with region information
        content.innerHTML = `<div class="card">
                          <div class="card-body">
                          <div class="headerRegionContent">${featuredtls.name}</div>
                          <div class="row">
                          <div class="col-12">
                            <label>Block</label>
                            <div>${featuredtls.block}</div>
                          </div>
                          <div class="col-12">
                            <label>District</label>
                            <div>${featuredtls.dist}</div>
                          </div>
                          <div class="col-12">
                            <label>Status</label>
                            <div>
                              ${statushtml}
                            </div>
                          </div>
                          </div>
                          </div>
                          </div>`;
        
        // Show popup at click location
        overlay.setPosition(evt.coordinate);
      } else {
        // Handle event feature click (WMS layer)
        showeventdtls(map, evt);
      }
    });
  };

  /**
   * Shows event details when clicking on event features (WMS layer)
   */
  const showeventdtls = async (map, evt) => {
    let view = map.getView();
    let viewResolution = view.getResolution();
    
    // Get feature info URL for WMS layer
    const url = eventlayer
      .getSource()
      .getFeatureInfoUrl(evt.coordinate, viewResolution, view.getProjection(), {
        INFO_FORMAT: "application/json",
        FEATURE_COUNT: 50,
      });
    
    
    // Fetch feature information from WMS
    const wfsdata = await getWFSLayer(url);
    
    // Extract event IDs from WFS response
    let eventids = [];
    wfsdata.features.forEach(element => {
      eventids.push({ 'id': element.properties.id });
    });
    
    // Get detailed event information
    const eventdetails = await postDataWithBodyFrGIS(
      "/getFeatures",
      { 'event': eventids },
      {}
    );
    
    
    // Get first event data if available
    let eventdata = eventdetails.event.data && eventdetails.event.data.length > 0 ? eventdetails.event.data[0] : null;
    
    if (eventdata) {
      // Parse location data
      const location = eventdata.Location ? JSON.parse(eventdata.Location) : {};
      
      // Populate popup with event information
      content.innerHTML = `<div class="card">
                          <div class="card-body">
                          <div class="headerRegionContent" style="text-align:center">Event Details</div>
                          <div class="row">
                          <div class="col-12">
                            <label>Id</label>
                            <div>${eventdata.EventId}</div>
                          </div>
                          <div class="col-12">
                            <label>Created By</label>
                            <div>${eventdata['Created By']}</div>
                          </div>
                          <div class="col-12">
                            <label>Location</label>
                            <div>
                              ${location.coordinates}
                            </div>
                          </div>
                          </div>
                          </div>
                          </div>`;
      
      // Show popup at click location
      overlay.setPosition(evt.coordinate);
    }
  };

  // ===== RENDER =====
  
  return (
    <>
      {/* Fullscreen wrapper */}
      <FullScreen
        handle={handle}
        className="nonfullscreen"
        onChange={reportChange}
      >
        <div className="row" style={{ height: "100%" }}>
          {/* Map container */}
          <div className={mapwidth}>
            {/* Map controls container (currently empty) */}
            <div className="mapcontrols">
            </div>
            
            {/* Main map element */}
            <div id="map" style={{ width: "100%", height: "100%" }}></div>
            
            {/* Popup overlay for feature information */}
            <div id="popup" class="ol-popup">
              <a href="#" id="popup-closer" className="ol-popup-closer"></a>
              <div id="popup-content"></div>
            </div>
          </div>
          
          {/* Information panel - collapsible */}
          <div
            id="mapinfo"
            className={infoWidth}
            style={{ display: infoStyle }}
          >
            <Accordion
              defaultActiveKey="0"
              style={{ width: "95%", color: "#ffff" }}
            >
              {/* Basic incident information section */}
              <Accordion.Item eventKey="0">
                <Accordion.Header>Basic Information</Accordion.Header>
                <Accordion.Body>
                  {incidentbasic && (
                    <div className="row">
                      <div className="col-12">
                        <label>Name</label>
                        <div>{incidentbasic.name}</div>
                      </div>
                      <div className="col-12">
                        <label>Category</label>
                        <div>{incidentbasic.cat}</div>
                      </div>
                      <div className="col-12">
                        <label>Time</label>
                        <div>{incidentbasic.time}</div>
                      </div>
                    </div>
                  )}
                </Accordion.Body>
              </Accordion.Item>
              
              {/* Affected regions section */}
              <Accordion.Item eventKey="1">
                <Accordion.Header>Affected Regions</Accordion.Header>
                <Accordion.Body
                  style={{
                    height: regionView,
                    overflow: "auto",
                    scrollbarWidth: "thin",
                  }}
                >
                  {/* Render each affected region as a card */}
                  {incidentregion &&
                    incidentregion.map((region) => (
                      <Card style={{ marginBottom: "10px" }}>
                        <Card.Body>
                          <Card.Title className="headerRegionContent">
                            {region.name}
                          </Card.Title>
                          <div className="row">
                            <div className="col-12">
                              <label>Block</label>
                              <div>{region.block}</div>
                            </div>
                            <div className="col-12">
                              <label>District</label>
                              <div>{region.dist}</div>
                            </div>
                            <div className="col-12">
                              <label>Status</label>
                              <div>
                                {/* Status badges for different incident levels */}
                                {region.code &&
                                  region.code === "INCIDENT_REGION_WARN" && (
                                    <span
                                      style={{
                                        backgroundColor: "#FF9633",
                                        color: "#000",
                                        borderRadius: "4px",
                                        padding: "0 5px 0 5px",
                                      }}
                                    >
                                      Warning
                                    </span>
                                  )}
                                {region.code &&
                                  region.code === "INCIDENT_REGION_NORMAL" && (
                                    <span
                                      style={{
                                        backgroundColor: "#93ea76",
                                        color: "#000",
                                        borderRadius: "4px",
                                        padding: "0 5px 0 5px",
                                      }}
                                    >
                                      Normal
                                    </span>
                                  )}
                                {region.code &&
                                  region.code === "INCIDENT_REGION_WATCH" && (
                                    <span
                                      style={{
                                        backgroundColor: "#FFEC33",
                                        color: "#000",
                                        borderRadius: "4px",
                                        padding: "0 5px 0 5px",
                                      }}
                                    >
                                      Watch
                                    </span>
                                  )}
                                {region.code &&
                                  region.code === "INCIDENT_REGION_AFFECT" && (
                                    <span
                                      style={{
                                        backgroundColor: "#FF5733",
                                        color: "#000",
                                        borderRadius: "4px",
                                        padding: "0 5px 0 5px",
                                      }}
                                    >
                                      Affected
                                    </span>
                                  )}
                                
                                {/* Status update notifications */}
                                {region.statusupdated && (
                                  <div className="blinking-text">
                                    Updated From {region.oldstatus}
                                  </div>
                                )}
                                {region.newregion && (
                                  <div className="blinking-text">
                                    New Region Added
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    ))}
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          </div>
        </div>
      </FullScreen>
    </>
  );
};

export default SOPInstanceOpenLayerSource;